import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Orbit, LogOut, Search, Download, Users, Target, ShieldCheck, RefreshCw, ExternalLink,
} from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BUSINESS_STATUSES = ["New", "Reviewing", "Approved", "Rejected", "Contacted"];
const CREATOR_STATUSES = ["New", "Reviewing", "Selected", "Waitlisted", "Rejected", "Contacted"];

const STATUS_COLORS = {
  New: "bg-sky-500/15 text-sky-300 border-sky-400/25",
  Reviewing: "bg-amber-500/15 text-amber-300 border-amber-400/25",
  Approved: "bg-emerald-500/15 text-emerald-300 border-emerald-400/25",
  Selected: "bg-emerald-500/15 text-emerald-300 border-emerald-400/25",
  Waitlisted: "bg-indigo-500/15 text-indigo-300 border-indigo-400/25",
  Rejected: "bg-red-500/15 text-red-300 border-red-400/25",
  Contacted: "bg-purple-500/15 text-purple-300 border-purple-400/25",
};

function Login() {
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <>
      <div className="app-bg" />
      <div className="min-h-screen grid place-items-center px-4">
        <div className="glass rounded-2xl p-8 sm:p-10 max-w-md w-full text-center">
          <span className="grid place-items-center h-12 w-12 mx-auto rounded-xl bg-gradient-to-br from-sky-500 to-blue-600">
            <Orbit size={22} className="text-white" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-bold text-white">Neo Orbit Admin</h1>
          <p className="mt-2 text-sm text-slate-400">Sign in to review Private Beta applications.</p>
          <button onClick={login} data-testid="admin-login-button" className="btn-primary w-full mt-7">
            Continue with Google
          </button>
          <Link to="/" className="block mt-4 text-xs text-slate-500 hover:text-slate-300">← Back to site</Link>
        </div>
      </div>
    </>
  );
}

function Denied({ email, onLogout }) {
  return (
    <>
      <div className="app-bg" />
      <div className="min-h-screen grid place-items-center px-4">
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
          <h1 className="font-display text-xl font-bold text-white">Not authorized</h1>
          <p className="mt-2 text-sm text-slate-400">{email} does not have admin access.</p>
          <button onClick={onLogout} className="btn-ghost mt-6">Sign out</button>
        </div>
      </div>
    </>
  );
}

function StatChip({ icon: Icon, label, value }) {
  return (
    <div className="glass rounded-xl p-4 flex items-center gap-3">
      <span className="grid place-items-center h-10 w-10 rounded-lg bg-sky-500/15 border border-sky-400/25 text-sky-300">
        <Icon size={18} />
      </span>
      <div>
        <p className="text-xl font-bold text-white leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("business");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const statuses = tab === "business" ? BUSINESS_STATUSES : CREATOR_STATUSES;

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/applications/${tab}`, {
        params: { status: statusFilter, search: search || undefined },
      });
      setRows(res.data);
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status !== 401 && e?.response?.status !== 403) toast.error("Failed to load applications");
    } finally {
      setBusy(false);
    }
  }, [tab, statusFilter, search]);

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user, load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${tab}/${id}/status`, { status });
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  const exportCsv = () => {
    window.open(`${API}/admin/applications/${tab}/export`, "_blank");
  };

  if (loading) {
    return (
      <>
        <div className="app-bg" />
        <div className="min-h-screen grid place-items-center text-slate-400 font-mono text-sm animate-pulse">Loading…</div>
      </>
    );
  }
  if (!user) return <Login />;
  if (!user.is_admin) return <Denied email={user.email} onLogout={logout} />;

  return (
    <>
      <div className="app-bg" />
      <div className="min-h-screen" data-testid="admin-dashboard-container">
        {/* Top bar */}
        <header className="border-b border-white/10 sticky top-0 z-20" style={{ background: "rgba(3,7,18,0.85)", backdropFilter: "blur(16px)" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center h-8 w-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600">
                <Orbit size={17} className="text-white" />
              </span>
              <span className="font-display font-bold text-white">Orbit Admin</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-xs text-slate-500 font-mono">{user.email}</span>
              <button onClick={logout} data-testid="admin-logout-button" className="btn-ghost !py-2 !px-3 !text-sm">
                <LogOut size={15} /> Sign out
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          {/* Stats */}
          {stats && (
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 mb-8">
              <StatChip icon={ShieldCheck} label="Total applications" value={stats.total} />
              <StatChip icon={Target} label="Business applications" value={stats.business_total} />
              <StatChip icon={Users} label="Creator applications" value={stats.creator_total} />
              <StatChip icon={ShieldCheck} label="Approved / Selected" value={stats.business_approved + stats.creator_selected} />
            </div>
          )}

          {/* Controls */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-5">
            <div className="inline-flex rounded-xl border border-white/10 bg-white/5 p-1 self-start">
              <button
                onClick={() => { setTab("business"); setStatusFilter("All"); }}
                data-testid="tab-business"
                className={`px-4 py-2 text-sm rounded-lg transition ${tab === "business" ? "bg-purple-500/20 text-white" : "text-slate-400"}`}
              >Businesses</button>
              <button
                onClick={() => { setTab("creator"); setStatusFilter("All"); }}
                data-testid="tab-creator"
                className={`px-4 py-2 text-sm rounded-lg transition ${tab === "creator" ? "bg-purple-500/20 text-white" : "text-slate-400"}`}
              >Creators</button>
            </div>

            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                data-testid="admin-search"
                className="glass-input !pl-9"
              />
            </div>

            <div className="flex items-center gap-2">
              <button onClick={load} className="btn-ghost !py-2 !px-3 !text-sm" data-testid="admin-refresh"><RefreshCw size={15} className={busy ? "animate-spin" : ""} /></button>
              <button onClick={exportCsv} className="btn-ghost !py-2 !px-3 !text-sm" data-testid="admin-export"><Download size={15} /> CSV</button>
            </div>
          </div>

          {/* Status filter pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            {["All", ...statuses].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`filter-${s.toLowerCase()}`}
                className={`px-3 py-1.5 text-xs rounded-full border transition ${
                  statusFilter === s ? "bg-purple-500/20 text-white border-purple-400/30" : "text-slate-400 border-white/10 hover:border-white/25"
                }`}
              >{s}</button>
            ))}
          </div>

          {/* Table */}
          <div className="glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/10">
                    {tab === "business" ? (
                      <>
                        <th className="px-5 py-3 font-medium">Business</th>
                        <th className="px-5 py-3 font-medium">Contact</th>
                        <th className="px-5 py-3 font-medium">Budget</th>
                        <th className="px-5 py-3 font-medium">Requirements</th>
                        <th className="px-5 py-3 font-medium">Date</th>
                        <th className="px-5 py-3 font-medium">Status</th>
                      </>
                    ) : (
                      <>
                        <th className="px-5 py-3 font-medium">Creator</th>
                        <th className="px-5 py-3 font-medium">Instagram</th>
                        <th className="px-5 py-3 font-medium">Category</th>
                        <th className="px-5 py-3 font-medium">Followers</th>
                        <th className="px-5 py-3 font-medium">Views</th>
                        <th className="px-5 py-3 font-medium">Eng.</th>
                        <th className="px-5 py-3 font-medium">Date</th>
                        <th className="px-5 py-3 font-medium">Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={tab === "business" ? 6 : 8} className="px-5 py-12 text-center text-slate-500">No applications found.</td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                      {tab === "business" ? (
                        <>
                          <td className="px-5 py-4">
                            <p className="text-white font-medium">{r.business_name}</p>
                            <p className="text-slate-500 text-xs">{r.full_name} · {r.industry}</p>
                          </td>
                          <td className="px-5 py-4 text-slate-300">
                            <p>{r.email}</p>
                            <p className="text-slate-500 text-xs">{r.phone} · {r.location}</p>
                          </td>
                          <td className="px-5 py-4 text-slate-300">{r.campaign_budget}</td>
                          <td className="px-5 py-4 text-slate-400 max-w-[220px] truncate" title={r.creator_requirements}>{r.creator_requirements}</td>
                          <td className="px-5 py-4 text-slate-500 whitespace-nowrap font-mono text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-5 py-4">
                            <p className="text-white font-medium">{r.full_name}</p>
                            <p className="text-slate-500 text-xs">{r.email} · {r.city}</p>
                          </td>
                          <td className="px-5 py-4 text-slate-300">
                            <a href={`https://instagram.com/${(r.instagram || "").replace("@", "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-purple-300 hover:underline">
                              {r.instagram} <ExternalLink size={11} />
                            </a>
                          </td>
                          <td className="px-5 py-4 text-slate-300">{r.creator_category}</td>
                          <td className="px-5 py-4 text-slate-300">{r.followers}</td>
                          <td className="px-5 py-4 text-slate-300">{r.average_reel_views}</td>
                          <td className="px-5 py-4 text-slate-300">{r.engagement_rate}</td>
                          <td className="px-5 py-4 text-slate-500 whitespace-nowrap font-mono text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                        </>
                      )}
                      <td className="px-5 py-4">
                        <select
                          value={r.status}
                          onChange={(e) => changeStatus(r.id, e.target.value)}
                          data-testid={`status-select-${r.id}`}
                          className={`text-xs rounded-full border px-2.5 py-1 bg-transparent outline-none cursor-pointer ${STATUS_COLORS[r.status] || "text-slate-300 border-white/15"}`}
                        >
                          {statuses.map((s) => <option key={s} value={s} className="bg-[#0b1120] text-white">{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
